/*
 * CollectionDetailPage — the reference lookbook detail, wired to the real
 * Collections API. DOM/classes/copy kept 1:1; demo FILES.clientFiles → the
 * collection's real items (resolved to models), with the piece-order badge kept.
 *
 * Wiring: getCollection(id) drives title/intro/share badge/view+download counts;
 * items (sorted by position) resolve to models; drag reorders via reorderItems;
 * each card's ✕ removes via removeItem; "Share collection" enables sharing and
 * surfaces the real /l/:shareToken link + client name / access controls;
 * "edit intro" persists via updateCollection; "Add pieces" opens the picker modal
 * (Agent B's AddItemsModal — hands it the collection id via modalData) and the
 * list refetches when it closes.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { promptDialog } from "@/shared/ui";
import { useApp } from "../vault/ctx";
import { FileCard, CopyBtn } from "../vault/Common";
import Ic from "../vault/Ic";
import {
  getCollection,
  updateCollection,
  removeItem,
  reorderItems,
  type Collection,
  type SharedAccess,
} from "@/features/collections/collectionsApi";
import { getModel, type VaultModel } from "@/features/vault/vaultApi";

export default function CollectionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { go, open, setModalData, modals } = useApp();

  const [col, setCol] = useState<Collection | null | undefined>(undefined);
  const [models, setModels] = useState<Record<string, VaultModel>>({});
  const [shareOpen, setShareOpen] = useState(false);
  const [drag, setDrag] = useState<number | null>(null);

  // Request-id guard: navigating between collections (or a slow load resolving after
  // a newer one) must not overwrite the current collection with stale data.
  const reqRef = useRef(0);
  const load = useCallback(async () => {
    if (!id) return;
    const req = ++reqRef.current;
    try {
      const c = await getCollection(id);
      if (reqRef.current !== req) return;
      setCol(c);
      const ids = [...c.items].sort((a, b) => a.position - b.position).map((i) => i.fileId);
      const resolved = await Promise.all(ids.map((fid) => getModel(fid).catch(() => null)));
      if (reqRef.current !== req) return;
      const map: Record<string, VaultModel> = {};
      resolved.forEach((m, i) => { if (m) map[ids[i]] = m; });
      setModels(map);
    } catch { if (reqRef.current === req) setCol(null); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  // Refetch after the Add-pieces picker (Agent B's AddItemsModal) closes.
  useEffect(() => { if (modals.addItems === false) load(); }, [modals.addItems, load]);

  if (col === undefined) return <div className="pg-empty" style={{ marginTop: 24 }}>Loading collection…</div>;
  if (col === null) {
    return (<>
      <div className="crumbs"><button className="crumb" onClick={() => go("collections")}>Collections</button> › <span className="here">Not found</span></div>
      <div className="pg-empty" style={{ marginTop: 16 }}>This collection couldn’t be loaded.</div>
    </>);
  }

  const ordered = [...col.items].sort((a, b) => a.position - b.position);
  const shareUrl = col.shareToken ? `${window.location.origin}/l/${col.shareToken}` : "";

  const saveShare = async (patch: Partial<{ shareEnabled: boolean; sharedClientName: string; sharedAccess: SharedAccess }>) => {
    try { setCol(await updateCollection(col._id, { shareEnabled: true, ...patch })); } catch { /* no-op */ }
  };
  const toggleShare = async () => {
    if (shareOpen) { setShareOpen(false); return; }
    if (!col.shareEnabled) { try { setCol(await updateCollection(col._id, { shareEnabled: true })); } catch { /* no-op */ } }
    setShareOpen(true);
  };
  const stopShare = async () => { try { setCol(await updateCollection(col._id, { shareEnabled: false })); setShareOpen(false); } catch { /* no-op */ } };
  const editIntro = async () => {
    const v = await promptDialog({ title: "Intro shown to clients", label: "Intro", defaultValue: col.intro || "" });
    if (v === null) return;
    try { setCol(await updateCollection(col._id, { intro: v })); } catch { /* no-op */ }
  };
  const previewLookbook = () => {
    if (col.shareEnabled && col.shareToken) window.open(shareUrl, "_blank", "noopener");
    else void toggleShare();
  };
  const addPieces = () => { setModalData("addItems", { collectionId: col._id }); open("addItems"); };
  const removePiece = async (fid: string) => { try { setCol(await removeItem(col._id, fid)); } catch { /* no-op */ } };

  const dropAt = async (target: number) => {
    if (drag === null || drag === target) { setDrag(null); return; }
    const ids = ordered.map((it) => it.fileId);
    const [moved] = ids.splice(drag, 1);
    ids.splice(target, 0, moved);
    setDrag(null);
    setCol({ ...col, items: ids.map((fid, idx) => ({ fileId: fid, position: idx })) }); // optimistic
    try { setCol(await reorderItems(col._id, ids)); } catch { load(); }
  };

  return (
    <>
      <div className="crumbs">
        <button className="crumb" onClick={() => go("collections")}>Collections</button> ›
        <span className="here">{col.name}</span>
        {col.shareEnabled && col.sharedClientName
          ? <span className="badge gold" style={{ marginLeft: 8 }}>Shared → {col.sharedClientName}</span>
          : col.shareEnabled
            ? <span className="badge green" style={{ marginLeft: 8 }}>Shared link</span>
            : <span className="badge gray" style={{ marginLeft: 8 }}>Private</span>}
        <span style={{ flex: 1 }}></span>
        <button className="btn ghost" onClick={addPieces}><Ic n="plus" size={13} sw={2} /> Add pieces</button>
        <button className="btn ghost" onClick={previewLookbook}><Ic n="eye" size={14} /> Preview lookbook</button>
        <button className="btn gold" onClick={toggleShare}><Ic n="link" size={13} /> Share collection</button>
      </div>

      <div className="page-sub" style={{ marginBottom: 4 }}>
        {col.intro ? <>“{col.intro}” · </> : <>No intro yet · </>}
        <a href="#" onClick={e => { e.preventDefault(); editIntro(); }} style={{ color: "var(--accent-strong)" }}>edit intro</a>
      </div>

      {shareOpen && (
        <div className="card" style={{ padding: "14px 16px", margin: "6px 0 12px" }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <input readOnly value={shareUrl} style={{ flex: 1, minWidth: 220, height: 38, border: "1px solid var(--border)", borderRadius: 10, padding: "0 12px", background: "var(--surface-2)", fontSize: 12.5 }} />
            <CopyBtn value={shareUrl} label="Copy client link" />
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 10, flexWrap: "wrap" }}>
            <input placeholder="Client name (optional)" defaultValue={col.sharedClientName || ""} onBlur={e => saveShare({ sharedClientName: e.target.value })} style={{ height: 34, border: "1px solid var(--border)", borderRadius: 9, padding: "0 10px", fontSize: 12.5 }} />
            <select value={col.sharedAccess} onChange={e => saveShare({ sharedAccess: e.target.value as SharedAccess })} style={{ height: 34, border: "1px solid var(--border)", borderRadius: 9, padding: "0 8px" }}>
              <option value="view">Can view</option>
              <option value="download">Can download</option>
              <option value="comment">Can comment</option>
              <option value="approve">Can approve</option>
            </select>
            <span style={{ flex: 1 }} />
            <button className="btn danger" style={{ padding: "6px 12px", fontSize: 12 }} onClick={stopShare}>Stop sharing</button>
          </div>
        </div>
      )}

      <div className="toolbar">
        <span className="drag-hint">⠿ Drag cards to set the story order — clients see them in this sequence</span>
        <div className="grow"></div>
        <span className="hint">{col.viewCount} views · {col.downloadCount} downloads · <a href="#" onClick={e => { e.preventDefault(); go("insights"); }} style={{ color: "var(--accent-strong)" }}>insights</a></span>
      </div>

      {ordered.length === 0 ? (
        <div className="pg-empty" style={{ marginTop: 8 }}>No pieces yet — use “Add pieces” to build this lookbook.</div>
      ) : (
        <div className="file-grid">
          {ordered.map((it, i) => {
            const m = models[it.fileId];
            const badge = (
              <>
                <span className="badge gold" style={{ position: "absolute", top: 8, left: 8, zIndex: 2 }}>⠿ {i + 1}</span>
                <button className="kebab" title="Remove from collection" style={{ position: "absolute", top: 6, right: 6, zIndex: 2, opacity: 1, background: "rgba(255,255,255,.92)" }} onClick={e => { e.stopPropagation(); removePiece(it.fileId); }}>✕</button>
              </>
            );
            return (
              <div key={it.fileId} draggable onDragStart={() => setDrag(i)} onDragOver={e => e.preventDefault()} onDrop={() => dropAt(i)} style={{ cursor: "grab" }}>
                {m ? (
                  <FileCard model={m} badge={badge} />
                ) : (
                  <div className="file-card">
                    {badge}
                    <div className="thumb" />
                    <div className="file-info">
                      <span className="mini-ico" style={{ background: "#5B5F66" }}>?</span>
                      <div><div className="fi-name">Unavailable piece</div><div className="fi-meta">This file was removed from your drive.</div></div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
